import React, { useState, useEffect } from "react";
import { Card, CardBody, Col, Button, Spinner, Row, Input } from "reactstrap";
import DataTable from "react-data-table-component";
import Breadcrumbs from "../../../CommonElements/Breadcrumbs";
import axios from "axios";
import useCurrency, { baseUrl } from "../../../Utils/FileUpload";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { FaFilePdf, FaFileExcel } from "react-icons/fa";
import moment from "moment";
import { exportToPDF } from "../../../Utils/pdfExportUtils";
import { excelExportUtils } from "../../../Utils/excelExportUtils";
import { useNavigate, useLocation } from "react-router-dom";
interface CallPayout {
  id: number;
  agent_id: number;
  agent_name: string;
  amount: string;
  callenddate: string;
  callstartdate: string;
  created_at: string;
  date: string;
  is_paid: number | string;
  isrequest: number;
  transaction_id: string;
  updated_at: string;
  amount_without_current: string;
  isFooter?: boolean;
}
export default function CallPayout() {
  const [filteredItems, setFilteredItems] = useState<CallPayout[]>([]);
  const [loading, setLoading] = useState(true);
  const token = localStorage.getItem("token");
  const [fromDate, setFromDate] = useState<Date | null>(null);
  const [toDate, setToDate] = useState<Date | null>(null);
  const { currency } = useCurrency();
  const [totalRows, setTotalRows] = useState<number>(0);
  const [perPage, setPerPage] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [agents, setAgents] = useState<{ id: number; agent_name: string }[]>(
    []
  );
  const [selectedAgent, setSelectedAgent] = useState("");
  const location = useLocation();
  const agentId = location.state?.agentId;
  let personTitle = "";

  if (agentId && filteredItems.length > 0) {
    personTitle = `${filteredItems[0].agent_name || "Agent"}'s Call Payout`;
  }
  // Fetch call payout data
  const [sortColumn, setSortColumn] = useState("created_at");
  const [sortOrder, setSortOrder] = useState("desc");

  const fetchData = async (page = 1, perPage = 10, sortField = sortColumn, sortDir = sortOrder) => {
    setLoading(true);
    try {
      const response = await axios.get(`${baseUrl}call_payroll/get_all`, {
        params: {
          page,
          per_page: perPage,
          from_date: fromDate ? fromDate.toISOString().split("T")[0] : null,
          to_date: toDate ? toDate.toISOString().split("T")[0] : null,
          agent_name: selectedAgent !== "" ? selectedAgent : null,
          agent_id: agentId || null,
          sort_by: sortField,
          sort_order: sortDir,
        },
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data: CallPayout[] = response.data.data;
      setFilteredItems(data);
      setTotalRows(response.data.total);
      setCurrentPage(response.data.current_page);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to fetch data");
    } finally {
      setLoading(false);
    }
  };

  // Fetch agents list
  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const result = await axios.get(`${baseUrl}agent/getall`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const filteredAgent = result.data.filter(
          (agent: any) => agent.is_deleted === 0 && agent.is_active === 1
        );
        setAgents(filteredAgent);
      } catch (error) {
        console.error("Error fetching agents:", error);
      }
    };
    fetchAgents();
  }, [token]);

  // Fetch payout data when filters change
  useEffect(() => {
    fetchData(currentPage, perPage);
  }, [currentPage, perPage, selectedAgent, fromDate, toDate]);

  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    fetchData(page, perPage, sortColumn, sortOrder);
  };

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setPerPage(newPerPage);
    setCurrentPage(1);
    fetchData(1, newPerPage, sortColumn, sortOrder);
  };

  const handleSort = (column: any, sortDirection: string) => {
    const sortField = column.sortField || "created_at";

    setSortColumn(sortField);
    setSortOrder(sortDirection);
    setCurrentPage(1);
    fetchData(1, perPage, sortField, sortDirection);
  };

  // Reset all filters
  const handleFullReset = () => {
    setFromDate(null);
    setToDate(null);
    setSelectedAgent("");
    setCurrentPage(1);
  };
  const totalAmount = filteredItems.reduce((sum, item) => {
    const cleanedAmount = (item.amount_without_current || "0").replace(
      /,/g,
      ""
    );
    const amt = parseFloat(cleanedAmount);
    return sum + (isNaN(amt) ? 0 : amt);
  }, 0);

  const formattedTotalAmount =
    (currency || "") +
    (Number.isInteger(totalAmount) ? totalAmount : totalAmount.toFixed(2));

  // Conditionally include the footer row
  const columnsData: CallPayout[] = [
    ...filteredItems,
    ...(filteredItems.length > 0
      ? [
          {
            id: -1,
            agent_id: -1,
            agent_name: "",
            amount: formattedTotalAmount,
            amount_without_current: "",
            callstartdate: "",
            callenddate: "",
            created_at: "",
            updated_at: "",
            date: "",
            is_paid: "",
            isrequest: 0,
            transaction_id: "",
            isFooter: true,
          },
        ]
      : []),
  ];
  const columns = [
    {
      name: <div style={{ whiteSpace: "normal" }}>S.No</div>,
      cell: (row: CallPayout, index: number) =>
        row.isFooter ? "" : (currentPage - 1) * perPage + index + 1,
      width: "80px",
    },
    {
      name: <div style={{ whiteSpace: "normal" }}>Agent Name</div>,
      selector: (row: CallPayout) => row.agent_name,
      sortable: true,
      sortField: "agent_name",
    },
    {
      name: <div style={{ whiteSpace: "normal" }}>Payment Date</div>,
      selector: (row: CallPayout) => row.date,
      sortable: true,
      sortField: "date",
    },
    {
      name: <div style={{ whiteSpace: "normal" }}>Call Start Date</div>,
      selector: (row: CallPayout) => row.callstartdate,
      sortable: true,
      sortField: "callstartdate",
    },
    {
      name: <div style={{ whiteSpace: "normal" }}>Call End Date</div>,
      selector: (row: CallPayout) => row.callenddate,
      sortable: true,
      sortField: "callenddate",
    },
    {
      name: <div style={{ whiteSpace: "normal" }}>Transaction ID</div>,
      selector: (row: CallPayout) => row.transaction_id,
      sortable: true,
      sortField: "transaction_id",
    },
    {
      name: <div style={{ whiteSpace: "normal" }}>Payment Status</div>,
      selector: (row: CallPayout) =>
        row.isFooter ? "Total" : row.is_paid === 1 ? "Paid" : "Unpaid",
      sortable: false,
      cell: (row: CallPayout) => {
        const isFooter = row.isFooter;
        const isPaid = row.is_paid === 1;

        const backgroundColor = isFooter
          ? "transparent"
          : isPaid
          ? "#d4edda" // light green
          : "#f8d7da"; // light red

        const color = isFooter
          ? "inherit"
          : isPaid
          ? "#155724" // dark green text
          : "#721c24"; // dark red text

        return (
          <span
            style={{
              fontWeight: isFooter ? "700" : "normal",
              backgroundColor,
              color,
              padding: "4px 8px",
              borderRadius: "10px",
              display: "inline-block",
            }}
          >
            {isFooter ? "Total" : isPaid ? "Paid" : "Unpaid"}
          </span>
        );
      },
    },
    {
      name: (
        <div style={{ whiteSpace: "normal" }}>
          Amount {currency ? `(${currency})` : ""}
        </div>
      ),
      selector: (row: CallPayout) => row.amount_without_current,
      sortable: true,
      sortField: "amount",
      cell: (row: CallPayout) => (
        <span style={{ fontWeight: row.isFooter ? "700" : "normal" }}>
          {row.isFooter ? row.amount : row.amount_without_current}
        </span>
      ),
    },
  ];
  const conditionalRowStyles = [
    {
      when: (row: any) => row.isFooter === true,
      style: {
        fontWeight: "800",
        backgroundColor: "#f8f9fa",
        color: "#308e87",
        borderTop: "2px solid #dee2e6",
        fontSize: "16px",
      },
    },
  ];
  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spinner color="primary" />
      </div>
    );
  }
  const fetchAllDataWithoutPagination = async () => {
    try {
      // console.log(fromDate, toDate, filterText);
      const response = await axios.get(
        `${baseUrl}call_payroll/get_all_without_pagination`,
        {
          params: {
            from_date: fromDate ? fromDate.toISOString().split("T")[0] : null, // Use the current from date
            to_date: toDate ? toDate.toISOString().split("T")[0] : null, // Use the current to date
            agent_name: selectedAgent !== "" ? selectedAgent : null, // ✅ Add this line
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      // console.log(response.data.data, "data");
      return response.data.data; // Return the fetched data
    } catch (error) {
      console.error("Error fetching all data without pagination:", error);
      return []; // Return an empty array on error
    }
  };
  const exportPDF = async () => {
    const rawData = await fetchAllDataWithoutPagination();
    const allData = Array.isArray(rawData) ? rawData : Object.values(rawData);

    const tableData = allData.map((row, index) => {
      return [
        index + 1,
        row.agent_name || "N/A",
        row.date || "N/A",
        row.callstartdate || "N/A",
        row.callenddate || "N/A",
        row.transaction_id || "N/A",
        row.is_paid === 1 ? "Paid" : "Unpaid",
        row.amount_without_current || "0",
      ];
    });

    const currencyMap: Record<string, string> = {
      "₹": "Rs.",
      "€": "EUR",
      "£": "GBP",
      "¥": "YEN",
      "₽": "RUB",
      "₩": "KRW",
      "฿": "THB",
      "₪": "ILS",
      "₫": "VND",
      "₱": "PHP",
    };
    const pdfCurrency = currencyMap[currency] || currency;

    const footerRow = [
      "",
      "",
      "",
      "",
      "",
      "",
      "Total",
      `${totalAmount}`,
    ];

    exportToPDF({
      fileName: "Call_Payout",
      columns: [
        "S.No",
        "Agent Name",
        "Payout Date",
        "Start Date",
        "End Date",
        "Trans ID",
        "Status",
        `Amount ${pdfCurrency ? `(${pdfCurrency})` : ""}`,
      ],
      data: tableData,
      emptyDataMessage: "No call payout available for export.",
      footerRow,
      autoTableOptions: {
        headStyles: { fillColor: [0, 105, 92] },
        styles: { fontSize: 8, cellPadding: 2 },
        columnStyles: {
          0: { halign: "center" },
          7: { halign: "right" },
        },
        didParseCell: (data: any) => {
          if (data.row.index === tableData.length) {
            data.cell.styles.fontStyle = "bold";
            data.cell.styles.fillColor = [245, 245, 245];
          }
        },
      },
    });
  };

  const exportExcel = async () => {
    const rawData = await fetchAllDataWithoutPagination();
    const allData = Array.isArray(rawData) ? rawData : Object.values(rawData);
    if (!allData.length) {
      toast.warn("No call report available for export.", {
        position: "top-right",
        autoClose: 1000,
        hideProgressBar: false,
        closeOnClick: true,
      });
      return;
    }

    const currencyMap: Record<string, string> = {
      "₹": "Rs.",
      "€": "EUR",
      "£": "GBP",
      "¥": "YEN",
      "₽": "RUB",
      "₩": "KRW",
      "฿": "THB",
      "₪": "ILS",
      "₫": "VND",
      "₱": "PHP",
    };
    const excelCurrency = currencyMap[currency] || currency;
    const amountHeader = `Amount ${excelCurrency ? `(${excelCurrency})` : ""}`;

    const tableData = allData.map((row: CallPayout, index: number) => {
      return {
        "S.No": index + 1,
        "Agent Name": row.agent_name || "",
        "Payment Date": row.date || "",
        "Call Start Date": row.callstartdate || "",
        "Call End Date": row.callenddate || "",
        "Transaction ID": row.transaction_id || "",
        "Payment Status": row.is_paid === 1 ? "Paid" : "Unpaid",
        [amountHeader]: row.amount_without_current || "0",
      };
    });

    const footerRow = {
      "S.No": "",
      "Agent Name": "",
      "Payment Date": "",
      "Call Start Date": "",
      "Call End Date": "",
      "Transaction ID": "",
      "Payment Status": "Total",
      [amountHeader]: totalAmount,
    };

    excelExportUtils(
      tableData,
      "Call_Payout_Report",
      "Call Payout Report",
      footerRow
    );
  };

  const customStyles = {
    headCells: {
      style: {
        fontSize: "16px",
        fontWeight: "600",
        color: "#308e87",
        backgroundColor: "#f8f9fa",
        borderBottom: "2px solid #308e87",
      },
    },
    rows: {
      style: {
        borderBottom: "1px solid #dee2e6",
      },
    },
    table: {
      style: {
        border: "1px solid #dee2e6",
        borderRadius: "8px",
      },
    },
  };

  return (
    <>
      <Breadcrumbs
        pageTitle="Call Payout"
        parent="Payouts"
        title="Call Payout"
      />
      <Col xl={12} className="mb-lg-3">
        <Card>
          <CardBody>
            {personTitle && (
              <h5
                style={{
                  marginBottom: "1rem",
                  color: "#308e87",
                  fontWeight: "600",
                }}
              >
                {personTitle}
              </h5>
            )}
            <Row className="align-items-center justify-content-end mb-3">
              <Col xl={2}>
                {!agentId && (
                  <div className="d-flex gap-2">
                    <Button
                      color="primary"
                      onClick={exportPDF}
                      className="d-flex justify-content-center align-items-center p-0"
                      style={{ width: "30px", height: "30px" }}
                      title="PDF"
                    >
                      <FaFilePdf size={16} />
                    </Button>
                    <Button
                      color="secondary"
                      onClick={exportExcel}
                      className="d-flex justify-content-center align-items-center p-0"
                      style={{ width: "30px", height: "30px" }}
                      title="EXCEL"
                    >
                      <FaFileExcel size={16} />
                    </Button>
                  </div>
                )}
              </Col>
              <Col xl={3}></Col>
              <Col xl={3}>
                {!agentId && (
                  <div className="d-flex align-items-center">
                    <Input
                      type="select"
                      value={selectedAgent}
                      onChange={(e) => {
                        setSelectedAgent(e.target.value);
                        setCurrentPage(1);
                      }}
                    >
                      <option value="">Filter by agent</option>
                      {agents.map((agent) => (
                        <option key={agent.id} value={agent.agent_name}>
                          {agent.agent_name}
                        </option>
                      ))}
                    </Input>
                  </div>
                )}
              </Col>

              <Col xl={4}>
                <div className="d-flex align-items-center gap-2">
                  <label className="mb-0">From:</label>
                  <div style={{ flex: 1 }}>
                    <DatePicker
                      selected={fromDate}
                      onChange={(date: Date | null) => {
                        setFromDate(date);
                        setCurrentPage(1);
                      }}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      placeholderText="Start date"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      maxDate={new Date()}
                    />
                  </div>

                  <label className="mb-0">To:</label>
                  <div style={{ flex: 1 }}>
                    <DatePicker
                      selected={toDate}
                      onChange={(date: Date | null) => {
                        setToDate(date);
                        setCurrentPage(1);
                      }}
                      dateFormat="yyyy-MM-dd"
                      className="form-control"
                      placeholderText="End date"
                      showMonthDropdown
                      showYearDropdown
                      dropdownMode="select"
                      maxDate={new Date()}
                    />
                  </div>
                </div>
              </Col>
            </Row>

            <div className="table-responsive custom-scrollbar">
              <DataTable
                data={columnsData}
                columns={columns}
                progressPending={loading}
                pagination
                paginationServer
                paginationTotalRows={totalRows}
                paginationPerPage={perPage}
                paginationDefaultPage={currentPage}
                onChangePage={handlePageChange}
                onChangeRowsPerPage={handlePerRowsChange}
                sortServer
                onSort={handleSort}
                highlightOnHover
                striped
                responsive
                conditionalRowStyles={conditionalRowStyles}
                customStyles={customStyles}
              />
            </div>
          </CardBody>
        </Card>
      </Col>

      <ToastContainer />
    </>
  );
}
